import { useLayoutEffect, useRef } from "react";

const DEFAULT_TOP_RANGE_PX = 88;
const DEFAULT_BOTTOM_RANGE_PX = 88;
const SURFACE_ATTR = "data-scroll-edge-surface";
const SURFACE_CLASS = "scroll-edge-surface";
const GLASS_PANEL = ".glass-panel, .glass-panel-strong";
const GLASS_PILL = ".glass-pill";
const STICKY_HEAD = ".table-sticky-head-bar, .list-sticky-head";
const TABLE_BODY = ".data-table-hscroll";

function edgeStrength(distancePx: number, rangePx: number): string {
  return Math.min(1, Math.max(0, distancePx) / rangePx).toFixed(3);
}

function edgeMaskPx(distancePx: number, rangePx: number): string {
  return `${Math.round(Math.min(rangePx, Math.max(0, distancePx)))}px`;
}

function readCssLengthPx(
  container: HTMLElement | null,
  variable: string,
  fallback: number,
): number {
  if (!container) return fallback;
  const raw = getComputedStyle(container).getPropertyValue(variable).trim();
  if (!raw) return fallback;

  const probe = document.createElement("div");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = raw.endsWith("px") ? raw : `calc(${raw})`;
  container.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  return px > 0 ? px : fallback;
}

function applyScrollEdgeVars(
  el: HTMLElement | null,
  top: string,
  bottom: string,
  topMask: string,
  bottomMask: string,
) {
  if (!el) return;
  el.style.setProperty("--scroll-edge-strength", top);
  el.style.setProperty("--scroll-edge-bottom-strength", bottom);
  el.style.setProperty("--scroll-edge-top-mask", topMask);
  el.style.setProperty("--scroll-edge-bottom-mask", bottomMask);
}

function clearSurfaceMask(el: HTMLElement) {
  el.removeAttribute(SURFACE_ATTR);
  el.classList.remove(SURFACE_CLASS);
  el.style.removeProperty("--scroll-surface-mask-h");
  el.style.removeProperty("--scroll-surface-mask-y");
}

function parseMaskPx(el: HTMLElement, variable: string): number {
  const raw = el.style.getPropertyValue(variable).trim();
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

function owningFrame(el: Element | null): HTMLElement | null {
  const frame = el?.closest(".scroll-fade-frame");
  return frame instanceof HTMLElement ? frame : null;
}

/** Surfaces inside a nested `.scroll-fade-frame` belong to that scroller. */
function isOwnedBy(scrollNode: HTMLElement, el: HTMLElement): boolean {
  return owningFrame(el) === owningFrame(scrollNode);
}

function isOutermostGlassPanel(el: HTMLElement): boolean {
  return !el.parentElement?.closest(GLASS_PANEL);
}

function collectMaskSurfaces(root: HTMLElement): HTMLElement[] {
  const panels = [...root.querySelectorAll<HTMLElement>(GLASS_PANEL)].filter(
    (el) =>
      isOwnedBy(root, el) &&
      isOutermostGlassPanel(el) &&
      !el.classList.contains("glass-panel-table") &&
      !el.querySelector(STICKY_HEAD),
  );
  const pills = [...root.querySelectorAll<HTMLElement>(GLASS_PILL)].filter(
    (el) => isOwnedBy(root, el) && !el.closest(GLASS_PANEL),
  );
  const tableBodies = [...root.querySelectorAll<HTMLElement>(TABLE_BODY)].filter(
    (el) => isOwnedBy(root, el),
  );
  return [...panels, ...pills, ...tableBodies];
}

function applySurfaceMasks(
  scrollNode: HTMLElement,
  active: boolean,
  surfaces: HTMLElement[],
) {
  const prev = scrollNode.querySelectorAll<HTMLElement>(`[${SURFACE_ATTR}]`);
  const fading =
    active &&
    (parseMaskPx(scrollNode, "--scroll-edge-top-mask") > 0 ||
      parseMaskPx(scrollNode, "--scroll-edge-bottom-mask") > 0);

  if (!fading) {
    for (const el of prev) clearSurfaceMask(el);
    return;
  }

  const port = scrollNode.getBoundingClientRect();
  const portHeight = `${Math.round(port.height)}px`;
  const seen = new Set(surfaces);

  for (const el of prev) {
    if (!seen.has(el)) clearSurfaceMask(el);
  }

  for (const el of surfaces) {
    const rect = el.getBoundingClientRect();
    el.setAttribute(SURFACE_ATTR, "");
    el.classList.add(SURFACE_CLASS);
    el.style.setProperty("--scroll-surface-mask-h", portHeight);
    el.style.setProperty(
      "--scroll-surface-mask-y",
      `${Math.round(port.top - rect.top)}px`,
    );
  }
}

/**
 * Drives top/bottom scroll-edge fades (0–1) from scroll position and overflow.
 * Pass `resetKey` when the scroll node is swapped (e.g. Kai welcome ↔ thread).
 */
export function useMainScrollEdge<T extends HTMLElement>(resetKey?: unknown) {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const frame = node.closest(".scroll-fade-frame") as HTMLElement | null;
    const mainLocked = Boolean(
      frame?.classList.contains("scroll-fade-frame--main-locked"),
    );
    const edgeToEdge = document.documentElement.classList.contains("edge-to-edge");
    const surfaceMask = Boolean(
      frame?.classList.contains("scroll-fade-frame--surface-mask") &&
        !mainLocked &&
        !edgeToEdge,
    );
    const noTopMask = Boolean(
      frame?.classList.contains("scroll-fade-frame--no-top-mask"),
    );

    let topRange = noTopMask
      ? 0
      : readCssLengthPx(frame, "--scroll-edge-top-range", DEFAULT_TOP_RANGE_PX);
    let bottomRange = readCssLengthPx(
      frame,
      "--scroll-edge-bottom-range",
      DEFAULT_BOTTOM_RANGE_PX,
    );
    let maskSurfaces = surfaceMask ? collectMaskSurfaces(node) : [];

    const refreshLayout = () => {
      topRange = noTopMask
        ? 0
        : readCssLengthPx(
            frame,
            "--scroll-edge-top-range",
            DEFAULT_TOP_RANGE_PX,
          );
      bottomRange = readCssLengthPx(
        frame,
        "--scroll-edge-bottom-range",
        DEFAULT_BOTTOM_RANGE_PX,
      );
      if (surfaceMask) maskSurfaces = collectMaskSurfaces(node);
    };

    const sync = () => {
      const remaining =
        node.scrollHeight - node.clientHeight - node.scrollTop;
      const top = edgeStrength(node.scrollTop, topRange);
      const bottom = edgeStrength(remaining, bottomRange);
      const topMask = noTopMask ? "0px" : edgeMaskPx(node.scrollTop, topRange);
      const bottomMask = edgeMaskPx(remaining, bottomRange);
      applyScrollEdgeVars(node, top, bottom, topMask, bottomMask);
      applyScrollEdgeVars(frame, top, bottom, topMask, bottomMask);
      if (surfaceMask) {
        applySurfaceMasks(node, true, maskSurfaces);
      }
    };

    let rafId = 0;
    const scheduleSync = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        sync();
      });
    };

    sync();
    node.addEventListener("scroll", scheduleSync, { passive: true });
    const ro = new ResizeObserver(() => {
      refreshLayout();
      scheduleSync();
    });
    ro.observe(node);
    for (const child of node.children) {
      if (child instanceof Element) ro.observe(child);
    }
    if (frame) ro.observe(frame);

    return () => {
      cancelAnimationFrame(rafId);
      node.removeEventListener("scroll", scheduleSync);
      ro.disconnect();
      if (surfaceMask) applySurfaceMasks(node, false, []);
    };
  }, [resetKey]);

  return ref;
}
